/**
 * @file Provides lazy loading for images
 *
 * Uses IntersectionObserver to wait for the element to become visible in the
 * viewport and then starts loading the image.
 *
 * The loading is done by initially setting the `[src]` and/or `[srcset]`
 * attribute to `[data-src]` and/or `[data-srcset]` and change it back to
 * `[src/srcset]` respectively once the img element is in the viewport.
 *
 * This includes images inside `<picture>` elements, with one or more `<source>`
 * elements.
 *
 * The image also gets a couple of CSS classes for doing animations such as a
 * short fade-in when the image has loaded. Do note that the observer is removed
 * as soon as the image has loaded and will only change the CSS classes once.
 *
 * Also note that the observer is applied to the actual `<img>` element, not to
 * the `<picture>` or `<figure>` element. Meaning: The `<img>` element must have
 * layout (not hidden). Any padding, margin etc does not apply to the observer.
 *
 * @author      Felix Dulfer    <fdulfer@intothesource.com>
 */

import { ILazyLoadableImageElement } from "./i-lazy-loadable-image-element";
import { ILazyLoadableSourceElement } from "./i-lazy-loadable-source-element";
import { TLazyLoadableElement } from "./t-lazy-loadable-element";
import { isPictureElement } from "./is-picture-element";

/**
 * Lazy loader Class
 *
 * Designed to play nice with IBHComponentController to let this class auto
 * instantiate when a route has loaded.
 */
export class LazyLoadImages {

    public static async init() {
        const ctrl = new this();
        await ctrl.init();
        return ctrl;
    }

    //
    // Events
    //

    public readonly EVENT_ERROR = "error";
    public readonly EVENT_LOAD = "load";

    //
    // CSS Classes
    //

    public readonly CLASS_NOT_LOADED = "not--loaded";
    public readonly CLASS_IS_LOADED = "is--loaded";
    public readonly CLASS_NOT_LOADING = "not--loading";
    public readonly CLASS_IS_LOADING = "is--loading";
    public readonly CLASS_NOT_FAILED = "not--failed";
    public readonly CLASS_IS_FAILED = "is--failed";

    //
    // Event handlers
    //

    private onErrorCallback$: any;
    private onLoadCallback$: any;

    /**
     * Global observer for all lazy-loadable images
     */
    private observer: IntersectionObserver;

    /**
     * Provides a check to see if this module _should_ load
     */
    static get shouldInit(): boolean {
        return this.prototype.images.length > 0;
    }

    /**
     * If the image gets within 100px in the Y axis, start the download.
     *
     * This gives just a little bit of headroom to start the loading right
     * before the element becomes visible, as if it was loaded already.
     */
    static get ioConfig(): IntersectionObserverInit {
        return {
            rootMargin: "100% 0px",
            threshold: 0.01,
        };
    }

    public async init(): Promise<void> {
        try {
            this.initImages();
        } catch (e) {
            // tslint:disable-next-line:no-console
            console.error(e);
            throw new Error(e);
        }
    }

    /**
     * Gets all images with the `data-lazy-load` attribute
     */
    private get images(): ILazyLoadableImageElement[] {
        const selector = "img[data-lazy-load]";
        const $images = document.querySelectorAll(selector) as NodeListOf<ILazyLoadableImageElement>;
        const images: ILazyLoadableImageElement[] = Array.prototype.slice.call($images);
        return images;
    }

    /**
     * Gets sibling `source` elements
     *
     * In case the image is sitting inside a `<picture>` element, we'll also
     * have to check that for any `[srcset]` or `[src]` attributes to let the
     * browser load the appropriate format for the current screen resolution.
     */
    private getSiblingSourceElms(image: ILazyLoadableImageElement): ILazyLoadableSourceElement[] {
        const { parentElement } = image;
        const sources: ILazyLoadableSourceElement[] = [];
        if (isPictureElement(parentElement)) {
            const $elms = parentElement.querySelectorAll("source") as
                NodeListOf<ILazyLoadableSourceElement>;
            const elms = Array.prototype.slice.call($elms);
            sources.push(...elms);
        }
        return sources;
    }

    /**
     * Sets the element `[src]` attribute if `src` is defined
     *
     * @param el
     * @param src
     */
    private setSrc(el: TLazyLoadableElement, src: string): void {
        if (src) { el.src = src; }
    }

    /**
     * Sets the element `[srcset]` attribute if `srcset` is defined
     *
     * @param el
     * @param srcset
     */
    private setSrcset(el: TLazyLoadableElement, srcset: string): void {
        if (srcset) { el.srcset = srcset; }
    }

    /**
     * Loads the actual source of the image and lets the browser figure out the
     * rest to load the appropriate image/resolution
     *
     * @param image
     */
    private loadImage(image: ILazyLoadableImageElement): void {
        // Remove the observer — no need for this once the image is shown
        this.observer.unobserve(image);
        // Get sibling `<source>` elements
        const sources = this.getSiblingSourceElms(image);
        // Update sibling source elements
        sources.map((source) => {
            this.setSrcset(source, source.dataset.srcset);
            this.setSrc(source, source.dataset.src);
        });
        // Let the image load
        this.setSrcset(image, image.dataset.srcset);
        this.setSrc(image, image.dataset.src);
    }

    /**
     * Callback for intersection observer
     *
     * This is invoked when one of the images has hit the threshold of being
     * or becoming visible in the viewport.
     *
     * @param entries
     */
    private onIntersection(entries: IntersectionObserverEntry[]): void {
        entries.map((entry) => {
            // If we're intersecting — need to check this because this event
            // will also fire when an element _leaves_ the viewport
            if (entry.intersectionRatio > 0) {
                this.loadImage(entry.target as ILazyLoadableImageElement);
            }
        });
    }

    /**
     * Sets initial state of CSS classes for an image before it is loaded
     *
     * @param image
     */
    private onBeforeLoad(image: ILazyLoadableImageElement): void {
        image.classList.remove(this.CLASS_IS_LOADED);
        image.classList.add(this.CLASS_IS_LOADING);
        image.classList.remove(this.CLASS_IS_FAILED);
        image.classList.add(this.CLASS_NOT_LOADED);
        image.classList.remove(this.CLASS_NOT_LOADING);
        image.classList.add(this.CLASS_NOT_FAILED);
    }

    /**
     * Sets error state CSS class when the image should load but failed to do so
     *
     * @param event
     */
    private onErrorCallback(event: Event): void {
        const image = event.target as ILazyLoadableImageElement;
        image.classList.add(this.CLASS_IS_LOADED);
        image.classList.remove(this.CLASS_IS_LOADING);
        image.classList.add(this.CLASS_IS_FAILED);
        image.classList.remove(this.CLASS_NOT_LOADED);
        image.classList.add(this.CLASS_NOT_LOADING);
        image.classList.remove(this.CLASS_NOT_FAILED);
    }

    /**
     * Sets loaded CSS classes to the image once it has actually loaded
     *
     * @param event
     */
    private onLoadCallback(event: Event): void {
        const image = event.target as ILazyLoadableImageElement;
        image.classList.add(this.CLASS_IS_LOADED);
        image.classList.remove(this.CLASS_IS_LOADING);
        image.classList.remove(this.CLASS_IS_FAILED);
        image.classList.remove(this.CLASS_NOT_LOADED);
        image.classList.add(this.CLASS_NOT_LOADING);
        image.classList.add(this.CLASS_NOT_FAILED);
    }

    /**
     * Binds callbacks to events from images and observers
     */
    private initImages(): void {
        const { images } = this;

        // Bind event callbacks to proto instance
        this.onErrorCallback$ = this.onErrorCallback.bind(this);
        this.onLoadCallback$ = this.onLoadCallback.bind(this);

        if (!this.observer) {
            // Create the observer
            this.observer = new IntersectionObserver(
                this.onIntersection.bind(this),
                LazyLoadImages.ioConfig,
            );
        }

        // Bind event listeners to images and bind image to observer
        images.map((image) => {
            if ((image.dataset as any).initialized === "true") {
                return;
            }
            this.onBeforeLoad(image);
            image.addEventListener(this.EVENT_ERROR, this.onErrorCallback$);
            image.addEventListener(this.EVENT_LOAD, this.onLoadCallback$);
            this.observer.observe(image);
            (image.dataset as any).initialized = "true";
        });
    }
}



// WEBPACK FOOTER //
// ./components/lazy-image/lazy-image.ts